import { useState, useEffect, useRef } from "react";
import { X, Lightbulb } from "lucide-react";
import { useUserPreferences, useDismissOnboardingHint } from "@/hooks/useUserPreferences";
import { useShouldSuppressDashboardHint } from "@/components/DashboardBanners";

/** Friendly page descriptions for first-time hints */
const pageHints: Record<string, string> = {
  "/": "This is your Dashboard: your command centre showing today's key numbers at a glance.",
  "/children": "This is the Children page: where you can find and manage children's profiles.",
  "/staff": "This is the Staff page: where you can view team members and their qualifications.",
  "/rota": "This is the Rota page: where you can view the week's shift schedule.",
  "/register": "This is the Register page: where attendance is recorded for the whole nursery.",
  "/attendance-staffing": "This is the Attendance page: where you can review attendance data over time.",
  "/transitions": "This is the Transitions page: where zone moves for children are tracked and managed.",
  "/planned-changes": "This is the Planned Changes page: where future changes to children's sessions are scheduled.",
  "/funding": "This is the Funding page: where you can manage government and private funding details.",
  "/reports": "This is the Reports page: where you can generate and export useful reports.",
  "/alerts": "This is the Alerts page: where you'll find notifications that need your attention.",
  "/compliance": "This is the Compliance page: where you can track staff qualifications and certificates.",
  "/approvals": "This is the Approvals page: where you review changes submitted by your team.",
  "/audit-log": "This is the Audit Log: a traceable record of every important action on the platform.",
  "/settings": "This is the Settings page: where platform configuration is managed.",
  "/security": "This is the Security & Trust page: where you can understand access levels and data protection.",
  "/access": "This is the Access Management page: where you can invite team members and manage roles.",
  "/my-profile": "This is your Profile page: where you can update your personal details and photo.",
  "/help": "This is the Help Manual: your complete guide to using Nursly.",
};

const VISIT_COUNT_KEY = "nursly_page_visit_counts";

function getVisitCounts(): Record<string, number> {
  try {
    const raw = localStorage.getItem(VISIT_COUNT_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function incrementVisit(pathname: string): number {
  const counts = getVisitCounts();
  counts[pathname] = (counts[pathname] || 0) + 1;
  localStorage.setItem(VISIT_COUNT_KEY, JSON.stringify(counts));
  return counts[pathname];
}

interface FirstTimeHintProps {
  pathname: string;
}

export function FirstTimeHint({ pathname }: FirstTimeHintProps) {
  const normPath = pathname.startsWith("/zone/") ? pathname : pathname;
  const hint = pageHints[normPath];
  const { data: prefs, isLoading } = useUserPreferences();
  const dismissHint = useDismissOnboardingHint();
  const suppressDashboard = useShouldSuppressDashboardHint();

  const visitCountRef = useRef<number>(0);

  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!hint || isLoading) {
      setVisible(false);
      return;
    }

    // Check server-side dismissal
    const serverDismissed = prefs?.onboarding_hints_dismissed as Record<string, boolean> | undefined;
    if (serverDismissed?.[normPath]) {
      setVisible(false);
      return;
    }

    // Auto-dismiss after 3 visits
    const count = incrementVisit(normPath);
    visitCountRef.current = count;
    if (count > 3) {
      setVisible(false);
      return;
    }

    // Dashboard suppression by higher-priority banners
    if (normPath === "/" && suppressDashboard) {
      setVisible(false);
      return;
    }

    setVisible(true);
  }, [normPath, hint, isLoading, prefs, suppressDashboard]);

  if (!visible || !hint) return null;

  const dismiss = () => {
    setVisible(false);
    dismissHint(normPath);
  };

  return (
    <div className="flex items-center gap-2 px-4 py-2.5 mb-4 rounded-lg border bg-[hsl(var(--info)/0.08)] border-[hsl(var(--info)/0.2)] text-sm">
      <Lightbulb className="h-4 w-4 text-[hsl(var(--info))] shrink-0" />
      <span className="flex-1 text-foreground">
        <span className="font-medium">First time here?</span> {hint}{" "}
        <span className="text-muted-foreground">Tap ❓ for help or dismiss this hint.</span>
      </span>
      <button
        onClick={dismiss}
        className="shrink-0 p-1 rounded hover:bg-muted transition-colors"
        aria-label="Dismiss hint"
      >
        <X className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
    </div>
  );
}
